/**
 * Listener de preview ao vivo do editor de tema (painel B2B admin).
 *
 * Ativação: só registra o listener quando a URL tem `?tema_preview=1`.
 * Isso garante que visitantes normais da loja NUNCA expõem esta API.
 *
 * Segurança (3 camadas):
 *   1. Query-gate: sem `?tema_preview=1`, listener nem é criado.
 *   2. Origin whitelist: mensagens de origem não autorizada são ignoradas.
 *      Lê de `VUE_APP_TEMA_PREVIEW_ORIGINS` (lista separada por vírgula).
 *      Em dev (localhost), aceita qualquer origem localhost por
 *      conveniência. Em prod, whitelist é obrigatória.
 *   3. Schema: valida `type === "tema.patch"` e `key` conforme regex.
 *      Nunca executa código arbitrário — só `store.commit("setSiteConfig")`.
 *
 * Fonte única da verdade para CSS vars de cor: `utils/theme-color-vars.js`
 * (mesmo util usado pela ação `setSiteConfig`). Isso garante que fallbacks
 * do tipo "--header-primary herda de --brand-primary quando vazio" se
 * comportem exatamente como no boot real da loja, sem duplicar regras.
 *
 * Protocolo:
 *   Pai → filho: { type: "tema.patch", key: "<siteconfig_key>", value: <any> }
 *   Filho → pai: { type: "tema.preview.ready" } (ao montar)
 *                { type: "tema.patch.ack",  key } (após cada patch)
 */

import { applyColorVars } from "@/utils/theme-color-vars";
import { getTypeMerge } from "@/components/tema-types";

const KEY_RE = /^[a-z0-9_]+$/;

function isAllowedOrigin(origin) {
  try {
    const host = new URL(origin).hostname;
    if (host === "localhost" || host === "127.0.0.1") return true;
    return host === "aglets.com.br" || host.endsWith(".aglets.com.br");
  } catch (_) {
    return false;
  }
}

/**
 * Detecta modo preview ativo: precisa (1) ter `?tema_preview=1` na URL,
 * (2) estar dentro de um iframe, e (3) que o parent (admin) seja uma origem
 * autorizada. Evita que um site malicioso incorpore a loja com o flag pra
 * disparar comportamento de preview (ex: forcar logout).
 *
 * Preferencia: window.location.ancestorOrigins (Chrome/Safari). Fallback:
 * document.referrer (outros navegadores). Sem info confiavel = nega.
 */
export function isPreview() {
  const qs = new URLSearchParams(window.location.search);
  if (qs.get("tema_preview") !== "1") return false;
  if (window.parent === window) return false;

  const aos = window.location.ancestorOrigins;
  if (aos && aos.length > 0 && aos[0]) {
    return isAllowedOrigin(aos[0]);
  }
  if (document.referrer) {
    try {
      return isAllowedOrigin(new URL(document.referrer).origin);
    } catch (_) {}
  }
  return false;
}


/**
 * Bloqueia qualquer tentativa de navegação no iframe de preview:
 *   - cliques em links (inclui Vue router-link, que renderiza <a>)
 *   - submits de formulário
 *   - alterações de history (router.push, location.href etc.)
 * O usuário pode ver/scrollar a loja livremente mas não sai da página.
 */
function installNavigationBlocker() {
  // installRouteSync já roteia cliques via router.push (preservando query
  // tema_preview=1 e filtrando por whitelist de rotas). Aqui tratamos as
  // escapadas programáticas que o router não cobre.

  function blockSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
  }
  document.addEventListener("submit", blockSubmit, true);

  // window.open em modo preview: bloqueia tudo — nenhum _blank sai do iframe.
  const origOpen = window.open;
  window.open = function() {
    // eslint-disable-next-line no-console
    console.warn("[tema-preview] window.open bloqueado em modo preview");
    return null;
  };
  // Mantém referência pro console conseguir debug se necessário.
  window.__origOpen = origOpen;

  // window.location.href/assign/replace: interceptamos via override do
  // objeto location. Browsers modernos permitem substituir os métodos
  // (não o property 'href' direto). Se o código chamar assign/replace,
  // redirecionamos pra rota de preview via router em vez de full navigate.
  // Set direto de window.location.href continua escapando — mas é raro e
  // o onIframeLoad handler do admin detecta e força reload pro preview.
  try {
    const origAssign = window.location.assign.bind(window.location);
    const origReplace = window.location.replace.bind(window.location);
    window.location.assign = function(url) {
      // eslint-disable-next-line no-console
      console.warn("[tema-preview] location.assign interceptado:", url);
    };
    window.location.replace = function(url) {
      // eslint-disable-next-line no-console
      console.warn("[tema-preview] location.replace interceptado:", url);
    };
    window.__origLocationAssign = origAssign;
    window.__origLocationReplace = origReplace;
  } catch (_) {
    // Alguns browsers congelam location — ignora.
  }

  // NÃO stubamos history.pushState/replaceState — Vue Router usa esses
  // métodos por baixo do router.push (usado pelo installRouteSync pra
  // sincronizar cliques em <a>). Stub-os-ia zerar o sync de URL no iframe.
  // Bypass externo via history API direta é cenário raro e o iframe está
  // isolado por origem — click/submit capture + router beforeEach já cobrem.
}


/**
 * Injeta CSS de highlight dos componentes editáveis. Só é chamado em modo
 * preview (`?tema_preview=1`), então nunca afeta visitantes reais da loja.
 * A classe `body.tema-preview-highlight` é ligada/desligada via mensagem
 * `tema.highlight` vinda do admin.
 */
/**
 * Toast posicionado perto do ultimo click, avisando que a rota nao esta
 * cadastrada no editor (navegacao bloqueada). O listener que popula
 * lastPointerPos é registrado em installTemaPreviewListener (só em modo
 * preview, nunca em visita real).
 */
let lastPointerPos = null;

function showNotEditableToast() {
  let el = document.getElementById("tema-preview-toast");
  if (el) el.remove();
  el = document.createElement("div");
  el.id = "tema-preview-toast";
  el.className = "tema-preview-toast";
  el.textContent = "Pagina nao disponivel no editor";

  if (lastPointerPos) {
    el.style.left = `${lastPointerPos.x}px`;
    el.style.top = `${Math.max(8, lastPointerPos.y - 44)}px`;
  } else {
    el.style.left = "50%";
    el.style.top = "24px";
  }

  document.body.appendChild(el);
  requestAnimationFrame(() => el.classList.add("tema-preview-toast-in"));
  setTimeout(() => el.classList.add("tema-preview-toast-out"), 1800);
  setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 2200);
}

/**
 * Sincroniza a rota da loja com o admin e bloqueia navegacao pra rotas que
 * nao estao cadastradas no admin (store.state.paginas). Rotas nativas (tipo
 * /produto/:id, /conta) ficam bloqueadas em modo preview.
 */
function installRouteSync(store, router) {
  if (!router || !store) return;

  const norm = (v) => `/${String(v || "").replace(/^\/+|\/+$/g, "")}`;

  // Intercepta cliques em <a href> pra rotear via Vue Router em vez de full
  // page navigation. Evita bypass do modo preview (?tema_preview=1 seria
  // perdido num navigate nativo). beforeEach entao decide se bloqueia.
  document.addEventListener("click", (e) => {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const anchor = e.target && e.target.closest && e.target.closest("a[href]");
    if (!anchor) return;
    if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;
    const href = anchor.getAttribute("href");
    if (!href || href.startsWith("#") || /^(mailto:|tel:|javascript:)/i.test(href)) return;
    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (_) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    // Link externo (outro dominio) em modo preview: bloqueia e avisa.
    if (url.origin !== window.location.origin) {
      showNotEditableToast();
      return;
    }
    const target = url.pathname + url.search + url.hash;
    // router.push passa pelo beforeEach que bloqueia se rota invalida.
    try { router.push(target); } catch (_) {}
  }, true);

  router.beforeEach((to, from, next) => {
    if (!from.name) return next();
    const paginas = (store.state && store.state.paginas) || [];
    const target = norm(to.path);
    const exists = paginas.some((p) => norm(p.rota) === target);
    if (exists) {
      next();
    } else {
      showNotEditableToast();
      next(false);
    }
  });

  router.afterEach((to) => {
    if (!(window.parent && window.parent !== window)) return;
    try {
      window.parent.postMessage(
        { type: "tema.navigated", path: to.path },
        "*"
      );
    } catch (_) {}
  });
}

function injectHighlightStyles() {
  if (document.getElementById("tema-preview-highlight-styles")) return;
  const style = document.createElement("style");
  style.id = "tema-preview-highlight-styles";
  style.textContent = `
    body.tema-preview-highlight [data-componente-id] {
      position: relative;
      cursor: pointer;
    }
    body.tema-preview-highlight [data-componente-id]:hover {
      outline: 3px solid rgba(33, 150, 243, 0.95);
      outline-offset: -3px;
    }
    body.tema-preview-highlight [data-componente-id]:hover::before {
      content: attr(data-componente-nome);
      position: absolute;
      top: 0;
      left: 0;
      background: rgba(33, 150, 243, 0.95);
      color: #fff;
      font-size: 11px;
      font-weight: 600;
      padding: 4px 10px;
      font-family: system-ui, -apple-system, sans-serif;
      letter-spacing: .02em;
      white-space: nowrap;
      border-radius: 0 0 4px 0;
      pointer-events: none;
      z-index: 150;
    }
    body.tema-preview-highlight [data-componente-id]:hover::after {
      content: "";
      position: absolute;
      inset: 0;
      background-color: rgba(33, 150, 243, 0.22);
      pointer-events: none;
      z-index: 100;
    }
    /* Botão "+" acima e abaixo de cada componente do body (secao_id=2). */
    .tema-add-btn {
      position: absolute;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: #1a73e8;
      color: #fff;
      border: 2px solid #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      line-height: 0;
      cursor: pointer;
      z-index: 200;
      box-shadow: 0 2px 6px rgba(0,0,0,.25);
      padding: 0;
      font-family: inherit;
      opacity: 0;
      transition: opacity .12s, transform .12s;
      pointer-events: auto;
    }
    body.tema-preview-highlight [data-componente-secao="2"]:hover > .tema-add-btn {
      opacity: 1;
    }
    .tema-add-btn:hover {
      transform: translate(-50%, -50%) scale(1.15);
    }
    .tema-add-btn-top { top: 0; }
    .tema-add-btn-bottom { top: 100%; }
    /* Botão "Editar" (lápis) canto superior direito. */
    .tema-menu-btn {
      position: absolute;
      top: 6px;
      right: 6px;
      width: 30px;
      height: 30px;
      border-radius: 6px;
      background: #1a73e8;
      color: #fff;
      border: 2px solid #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 200;
      box-shadow: 0 2px 6px rgba(0,0,0,.2);
      padding: 0;
      opacity: 0;
      transition: opacity .12s, background .12s;
      pointer-events: auto;
    }
    body.tema-preview-highlight [data-componente-id]:hover > .tema-menu-btn {
      opacity: 1;
    }
    .tema-menu-btn:hover {
      background: #1557b0;
    }
    .tema-menu-btn svg {
      width: 16px;
      height: 16px;
    }
    /* Toast proximo ao ultimo click. position/left/top setados inline no JS. */
    .tema-preview-toast {
      position: fixed;
      background: rgba(220, 53, 69, .98);
      color: #fff;
      padding: 8px 14px 8px 30px;
      border-radius: 6px;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: .01em;
      box-shadow: 0 4px 14px rgba(0,0,0,.3);
      z-index: 100001;
      pointer-events: none;
      white-space: nowrap;
      opacity: 0;
      transform: translate(-50%, 4px);
      transition: opacity .15s ease, transform .15s ease;
    }
    .tema-preview-toast::before {
      content: "⚠";
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 13px;
    }
    .tema-preview-toast-in {
      opacity: 1;
      transform: translate(-50%, 0);
    }
    .tema-preview-toast-out {
      opacity: 0;
      transform: translate(-50%, -4px);
    }
    /* Componente sendo editado no admin — overlay persistente, distinto do hover. */
    body.tema-preview-highlight [data-componente-id].tema-is-selected {
      outline: 3px solid #1a73e8;
      outline-offset: -3px;
    }
    body.tema-preview-highlight [data-componente-id].tema-is-selected::before {
      content: "Editando: " attr(data-componente-nome);
      position: absolute;
      top: 0;
      left: 0;
      background: #1a73e8;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      padding: 4px 10px;
      font-family: system-ui, -apple-system, sans-serif;
      letter-spacing: .02em;
      white-space: nowrap;
      border-radius: 0 0 4px 0;
      pointer-events: none;
      z-index: 160;
    }
    body.tema-preview-highlight [data-componente-id].tema-is-selected::after {
      content: "";
      position: absolute;
      inset: 0;
      background: rgba(26, 115, 232, 0.08);
      pointer-events: none;
      z-index: 100;
    }
    /* Mantém o botão de editar visivel enquanto selecionado. */
    body.tema-preview-highlight [data-componente-id].tema-is-selected > .tema-menu-btn {
      opacity: 1;
    }
    /* Componente ativo mas sem conteúdo visível — mostra placeholder
       pra confirmar que ele existe naquele lugar. */
    body.tema-preview-highlight [data-componente-id].tema-is-empty {
      min-height: 72px;
    }
    .tema-empty-placeholder {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 72px;
      padding: 16px 20px;
      background: repeating-linear-gradient(
        45deg,
        rgba(26, 115, 232, 0.05),
        rgba(26, 115, 232, 0.05) 10px,
        rgba(26, 115, 232, 0.02) 10px,
        rgba(26, 115, 232, 0.02) 20px
      );
      border: 1px dashed rgba(26, 115, 232, 0.45);
      color: #1a5ba8;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: .02em;
      pointer-events: none;
      z-index: 1;
    }
    .tema-empty-placeholder::before {
      content: "◇";
      font-size: 14px;
      opacity: .7;
    }
  `;
  document.head.appendChild(style);
}

function createMenuBtn(wrapper) {
  const btn = document.createElement("button");
  btn.className = "tema-menu-btn";
  btn.type = "button";
  btn.setAttribute("aria-label", "Opções do componente");
  btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>';
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!(window.parent && window.parent !== window)) return;
    const id = Number(wrapper.getAttribute("data-componente-id"));
    if (!id) return;
    try {
      window.parent.postMessage({ type: "tema.editComponent", componenteId: id }, "*");
    } catch (_) {}
  });
  return btn;
}

function createAddBtn(wrapper, position) {
  const btn = document.createElement("button");
  btn.className = `tema-add-btn tema-add-btn-${position}`;
  btn.type = "button";
  btn.setAttribute("aria-label", position === "top" ? "Adicionar componente acima" : "Adicionar componente abaixo");
  btn.textContent = "+";
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!(window.parent && window.parent !== window)) return;
    const secaoId = Number(wrapper.getAttribute("data-componente-secao"));
    const ordem = Number(wrapper.getAttribute("data-componente-ordem"));
    const relId = Number(wrapper.getAttribute("data-componente-id"));
    try {
      window.parent.postMessage({
        type: "tema.addComponent",
        secaoId,
        position,
        relativeTo: relId,
        relativeOrdem: ordem,
      }, "*");
    } catch (_) {}
  });
  return btn;
}

let selectedComponenteId = null;

function applySelectedClass() {
  document.querySelectorAll("[data-componente-id].tema-is-selected").forEach((el) => {
    el.classList.remove("tema-is-selected");
  });
  if (selectedComponenteId == null) return;
  const safeId = String(selectedComponenteId).replace(/[^0-9]/g, "");
  if (!safeId) return;
  document.querySelectorAll(`[data-componente-id="${safeId}"]`).forEach((el) => {
    el.classList.add("tema-is-selected");
  });
}

// Verifica se um componente tem conteúdo real (ignora elementos que nós mesmos
// injetamos: botões de edit/add e o placeholder de empty). Se não tem,
// adiciona um placeholder visual pra sinalizar ao usuário que o componente
// existe mas está vazio.
const INJECTED_CLASSES = [
  "tema-menu-btn",
  "tema-add-btn",
  "tema-add-btn-top",
  "tema-add-btn-bottom",
  "tema-empty-placeholder",
];

function getRealContentHeight(el) {
  let total = 0;
  for (const child of el.children) {
    if (INJECTED_CLASSES.some((c) => child.classList.contains(c))) continue;
    total += child.getBoundingClientRect().height;
  }
  return total;
}

function applyEmptyPlaceholders() {
  if (document.body.getBoundingClientRect().height === 0) return;

  const candidatos = [];
  document.querySelectorAll("[data-componente-id]").forEach((el) => {
    const realH = getRealContentHeight(el);
    const existing = el.querySelector(":scope > .tema-empty-placeholder");
    if (realH >= 20) {
      el.classList.remove("tema-is-empty");
      if (existing) existing.remove();
    } else if (!existing) {
      candidatos.push(el);
    }
  });

  if (candidatos.length === 0) return;

  requestAnimationFrame(() => {
    candidatos.forEach((el) => {
      if (getRealContentHeight(el) >= 20) return;
      el.classList.add("tema-is-empty");
      if (el.querySelector(":scope > .tema-empty-placeholder")) return;
      const nome = el.getAttribute("data-componente-nome") || "Componente";
      const ph = document.createElement("div");
      ph.className = "tema-empty-placeholder";
      ph.textContent = `${nome} — ativo, sem conteúdo para o contexto atual`;
      el.appendChild(ph);
    });
  });
}

function injectComponentButtons() {
  document.querySelectorAll("[data-componente-id]").forEach((el) => {
    if (!el.querySelector(":scope > .tema-menu-btn")) {
      el.appendChild(createMenuBtn(el));
    }
    if (el.getAttribute("data-componente-secao") === "2") {
      if (!el.querySelector(":scope > .tema-add-btn-top")) {
        el.appendChild(createAddBtn(el, "top"));
      }
      if (!el.querySelector(":scope > .tema-add-btn-bottom")) {
        el.appendChild(createAddBtn(el, "bottom"));
      }
    }
  });
  applySelectedClass();
  applyEmptyPlaceholders();
}

let buttonObserver = null;
function setupButtonObserver() {
  if (buttonObserver) return;
  injectComponentButtons();
  let pending = false;
  buttonObserver = new MutationObserver(() => {
    if (pending) return;
    pending = true;
    setTimeout(() => {
      pending = false;
      injectComponentButtons();
    }, 80);
  });
  buttonObserver.observe(document.body, { childList: true, subtree: true });
}


export function installTemaPreviewListener(store, router) {
  try {
    if (!isPreview()) return;
    // Idempotência — HMR em dev (ou re-mount de App) chama o install
    // duas vezes. Sem guard, registramos listeners duplicados e o
    // re-stub de location.assign/replace vira recursivo.
    if (window.__temaPreviewInstalled) return;
    window.__temaPreviewInstalled = true;

    injectHighlightStyles();
    installNavigationBlocker();
    installRouteSync(store, router);
    // pointerdown capture é só necessário em modo preview (usado pelo
    // toast de rota bloqueada pra posicionar perto do click). Registrado
    // aqui, não no top-level do módulo, pra não rodar em clientes reais.
    document.addEventListener("pointerdown", (e) => {
      lastPointerPos = { x: e.clientX, y: e.clientY };
    }, true);

    window.addEventListener("message", (event) => {
      if (!isAllowedOrigin(event.origin)) return;

      const data = event.data || {};

      if (data.type === "tema.highlight") {
        document.body.classList.toggle("tema-preview-highlight", !!data.on);
        if (data.on) {
          setupButtonObserver();
        } else if (buttonObserver) {
          // Sem disconnect, o observer continua rodando subtree/childList em
          // todo o body mesmo com highlight desligado — custo real em páginas
          // com muito DOM dinâmico (carousels, produtos sendo renderizados).
          buttonObserver.disconnect();
          buttonObserver = null;
        }
        return;
      }

      if (data.type === "tema.selectedComponent") {
        const id = data.id;
        selectedComponenteId = id == null ? null : Number(id);
        applySelectedClass();
        // Se o admin pediu scroll, centraliza o componente selecionado na viewport.
        if (data.scroll && selectedComponenteId != null) {
          const safeId = String(selectedComponenteId).replace(/[^0-9]/g, "");
          if (safeId) {
            const target = document.querySelector(`[data-componente-id="${safeId}"]`);
            if (target && typeof target.scrollIntoView === "function") {
              target.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }
        }
        return;
      }

      if (data.type === "tema.refetchComponents") {
        store.dispatch("fetchAllComponents");
        window.dispatchEvent(new Event("tema:refetch-componentes"));
        return;
      }

      if (data.type === "tema.updateComponent") {
        const componenteId = Number(data.componenteId);
        if (!componenteId) return;
        const rawPayload = data.payload || {};
        const current = (store.state.allComponents || []).find(
          (c) => Number(c.componente_id) === componenteId
        );
        if (!current) return; // componente não existe na página atual — ignora
        const tipo = Number(current.tipo_id);
        const merge = getTypeMerge(tipo);
        const payload = merge ? merge(current, rawPayload) : rawPayload;
        try {
          store.commit("temaPreviewUpdateComponent", { componenteId, payload });
        } catch (e) {
          console.warn("[tema-preview] updateComponent falhou:", e);
        }
        // Tipo 8 (categorias) tem espelho num módulo Vuex separado
        // (catalogo.componente_categorias) que HeaderMenu.vue lê. Admin
        // envia o array já no shape aninhado via normalize() do tipo 8.
        if (tipo === 8 && Array.isArray(payload.componente_categorias)) {
          try {
            store.dispatch("setCategoriasComponente", payload.componente_categorias);
          } catch (e) {
            console.warn("[tema-preview] setCategoriasComponente falhou:", e);
          }
        }
        return;
      }

      if (data.type === "tema.addComponent") {
        const componente = data.componente;
        if (!componente || !componente.componente_id) return;
        try {
          store.commit("temaPreviewAddComponent", { componente });
        } catch (e) {
          console.warn("[tema-preview] addComponent falhou:", e);
        }
        return;
      }

      if (data.type === "tema.removeComponent") {
        const componenteId = Number(data.componenteId);
        if (!componenteId) return;
        try {
          store.commit("temaPreviewRemoveComponent", { componenteId });
        } catch (e) {
          console.warn("[tema-preview] removeComponent falhou:", e);
        }
        return;
      }

      if (data.type === "tema.reorderComponents") {
        const secaoId = Number(data.secaoId);
        const ordem = Array.isArray(data.ordem) ? data.ordem : [];
        if (!secaoId || !ordem.length) return;
        try {
          store.commit("temaPreviewReorderComponents", { secaoId, ordem });
        } catch (e) {
          console.warn("[tema-preview] reorderComponents falhou:", e);
        }
        return;
      }

      if (data.type !== "tema.patch") return;

      const key = String(data.key || "");
      if (!KEY_RE.test(key)) return;

      const value = data.value;

      let updated = {};
      try {
        const current = (store.getters && store.getters.siteConfig) || {};
        updated = { ...current, [key]: value };
        store.commit("setSiteConfig", updated);
      } catch (e) {
        // swallow — preview não deve quebrar a loja
        // eslint-disable-next-line no-console
        console.warn("[tema-preview] commit falhou:", e);
        return;
      }

      // Reaplica TODAS as CSS vars de cor usando as mesmas regras do
      // setSiteConfig real. Isso garante que ao mexer em brand_primary,
      // os derivados (header-primary, button-primary, etc.) recalculem
      // com o fallback correto.
      if (key.startsWith("app_color_") || key === "app_color_brand_primary") {
        try {
          applyColorVars(updated);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[tema-preview] applyColorVars falhou:", e);
        }
      }

      try {
        if (event.source && event.source.postMessage) {
          event.source.postMessage(
            { type: "tema.patch.ack", key },
            event.origin
          );
        }
      } catch (_) {}
    });

    // Avisa o pai que estamos prontos pra receber patches
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage({ type: "tema.preview.ready" }, "*");
      } catch (_) {}
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[tema-preview] install falhou:", e);
  }
}

export default installTemaPreviewListener;
